/**
 * Angels Contract — single module for design tokens consumed by TypeScript.
 *
 * Browser-facing CSS custom properties live in `app/globals.css` (`:root` / `.dark`).
 * Keep numeric and formula values in sync: this file is the canonical reference for
 * tokens used in TS (Tailwind config, PDF preview injection, tests).
 *
 * Sections: highlight chroma → field focus (semantic) → motion → form layout.
 */

/** Comma-separated channels for `rgba(var(--ds-highlight-bright-rgb), alpha)` — matches `angels-lightblue`. */
export const DS_HIGHLIGHT_BRIGHT_RGB_TRIPLET = "131, 208, 242";

/** Comma-separated channels for `rgb(var(--ds-highlight-deep-rgb))` — ring / deep accent. */
export const DS_HIGHLIGHT_DEEP_RGB_TRIPLET = "56, 132, 191";

/** CSS custom property name (no value) — bright channel. */
export const DS_HIGHLIGHT_CSS_VAR_BRIGHT = "--ds-highlight-bright-rgb";

/** CSS custom property name (no value) — deep channel. */
export const DS_HIGHLIGHT_CSS_VAR_DEEP = "--ds-highlight-deep-rgb";

/**
 * Right-hand side for `:root { --highlight-bg: ... }` — must match `app/globals.css` (light).
 * Derived from {@link DS_HIGHLIGHT_BRIGHT_RGB_TRIPLET}.
 */
export const DS_FIELD_HIGHLIGHT_BG_CSS_VALUE =
  "rgba(var(--ds-highlight-bright-rgb), 0.22)";

/**
 * Right-hand side for `:root { --highlight-border: ... }` — must match `app/globals.css`.
 * Derived from {@link DS_HIGHLIGHT_DEEP_RGB_TRIPLET}.
 */
export const DS_FIELD_HIGHLIGHT_BORDER_CSS_VALUE =
  "rgb(var(--ds-highlight-deep-rgb))";

/**
 * Host / iframe chip: same as `.pdf-highlight-target.active-highlight` in `PREVIEW_OVERRIDE_STYLE`.
 */
export const DS_HOST_PREVIEW_HIGHLIGHT_SURFACE_CLASS =
  "bg-[var(--highlight-bg)] shadow-[0_0_0_1px_var(--highlight-border)]";

/**
 * Transient **page/sheet** pulse in the preview column: same 1px border token as words, no fill.
 */
export const DS_HOST_PREVIEW_PAGE_HIGHLIGHT_BORDER_CLASS =
  "shadow-[0_0_0_1px_var(--highlight-border)]";

/** Dark theme `--highlight-bg` value — must match `app/globals.css` `.dark`. */
export const DS_FIELD_HIGHLIGHT_BG_CSS_VALUE_DARK_THEME =
  "rgba(var(--ds-highlight-bright-rgb), 0.2)";

/** Full ViaCEP / highlight pulse duration (ms). Matches `--ds-highlight-duration` in globals. */
export const DS_HIGHLIGHT_DURATION_MS = 2500;

/** Host preview document ring at full strength before fade-out (tabs / optional attachments). */
export const DS_PREVIEW_DOCUMENT_TRANSIENT_HIGHLIGHT_MS = 1500;

/** CSS / timeout for smooth removal of that ring — keep in sync with preview shell `transition-*`. */
export const DS_PREVIEW_DOCUMENT_HIGHLIGHT_EXIT_MS = 350;

/**
 * HSL channels only (no `hsl()` wrapper) — light theme primary; use where theme vars are unavailable.
 * Matches `app/globals.css` `:root` `--primary`.
 */
export const DS_PRIMARY_HSL_CHANNELS = "234 34% 48%";

/** Use with `useMediaQuery` — must stay in sync with Tailwind `lg:` (1024px). */
export const DS_MEDIA_QUERY_LG_MIN = "(min-width: 1024px)" as const;

/** Garamond stack for print HTML and parity with `public/contract-template.html`. */
export const DS_DOCUMENT_PDF_FONT_FAMILY_STACK =
  '"WUKUIO+Garamond", Garamond, "Times New Roman", Times, serif';

/**
 * Angels logo — **contract principal** is canonical (`contract-logo` / `public/contract-template.html`).
 * Preview uses max width in `rem`; print/PDF uses width in `mm` (same visual pairing as 11.25rem ↔ 40mm).
 */
export const documentLogoTokens = {
  previewMaxWidthRem: 11.25,
  printWidthMm: 40,
  /**
   * Intrinsic aspect ratio (height ÷ width) of `public/angels-logo.png` (945×858).
   * `.doc-logo` sets `width` + `height: auto`, so printed logo height is `printWidthMm × this`.
   * Used by the single-page budget guard in `attachment-page-budget.ts`.
   */
  intrinsicAspectRatio: 858 / 945,
} as const;

/** Print width of the Pesquisa de Satisfação letterhead — narrower than the shared 40mm mark. */
export const DS_SURVEY_LOGO_PRINT_WIDTH_MM = 34;

/**
 * Preview counterpart of {@link DS_SURVEY_LOGO_PRINT_WIDTH_MM}, scaled from the shared
 * `printWidthMm ↔ previewMaxWidthRem` pairing so screen and print keep the same proportion.
 */
export const DS_SURVEY_LOGO_PREVIEW_MAX_WIDTH_REM = parseFloat(
  (
    (DS_SURVEY_LOGO_PRINT_WIDTH_MM / documentLogoTokens.printWidthMm) *
    documentLogoTokens.previewMaxWidthRem
  ).toFixed(3)
);

/**
 * Preview iframe: `body { zoom: min(1, calc(100vw / …)) }` in `PREVIEW_OVERRIDE_STYLE`.
 * Attachments (React on host) reuse the same divisor with `100cqw` + `container-type: inline-size`.
 */
export const DS_DOCUMENT_PREVIEW_ZOOM_REFERENCE_WIDTH_REM = 49.625;

/**
 * Screen preview base font size on `.contract-doc-inner` — matches `public/contract-template.html`.
 */
export const DS_DOCUMENT_PREVIEW_INNER_FONT_SIZE_EM = 1.170044;

/**
 * Preview body line height for the **main contract** iframe (`main.contract-document p` / `PREVIEW_OVERRIDE_STYLE`).
 * Attachments use {@link DS_ATTACHMENT_PREVIEW_BODY_LINE_HEIGHT} for a denser sheet.
 */
export const DS_DOCUMENT_PREVIEW_BODY_LINE_HEIGHT = "1.22" as const;

/**
 * Attachment React preview body line-height (em ratio) — tighter than contract clause preview
 * ({@link DS_DOCUMENT_PREVIEW_BODY_LINE_HEIGHT}) to save vertical space on Abatimento / Pendrive / Cachê.
 */
export const DS_ATTACHMENT_PREVIEW_BODY_LINE_HEIGHT = "1.14" as const;

/**
 * Pendrive vertical bands — **same `em` values** in React preview (`pendrive*MarginClass`) and
 * print/PDF (`attachment-print-html` `.pendrive-declaracao` / `.pendrive-local-date`).
 * Derived from preview body line-height so “N linhas” matches between telas e impressão.
 */
export const DS_PENDRIVE_TWO_LINE_SPACER_EM = parseFloat(
  (2 * Number(DS_DOCUMENT_PREVIEW_BODY_LINE_HEIGHT)).toFixed(2)
);
export const DS_PENDRIVE_THREE_LINE_SPACER_EM = parseFloat(
  (3 * Number(DS_DOCUMENT_PREVIEW_BODY_LINE_HEIGHT)).toFixed(2)
);

/**
 * ## Document preview — vertical rhythm (conceptual map)
 *
 * The main contract iframe does **not** use one spacing for everything; it uses **roles**:
 *
 * | Role | Meaning | DS keys |
 * |------|---------|---------|
 * | **BodyParagraph** | Default `p` stack in preview | `documentPreviewContractIframeRhythm.mainParagraphMarginBottom` |
 * | **ClauseParagraph** | `.contract-clause` — more air between numbered clauses | `mainClauseParagraphMarginBottom` |
 * | **SubClauseFirst** | First `p` inside `.contract-clause-sub-keep` (label glued to body) | `clauseSubFirstMarginBottom` |
 * | **ContatosBlock** | Technical contact stack (symmetric block margin + tight lines) | `contatosBlockMarginEm`, `contatosLineGap`, `contatosInnerLineHeight`, … |
 * | **ModelQualInner** | Lines inside a model qualification | `contractLayout.modelQualInnerGapPreviewEm`, `modelQualInnerLineHeight` |
 * | **ModelQualBetween** | Space between MODELO blocks | `contractLayout.modelQualBetweenBlocksPreviewEm` |
 *
 * **PDF** uses `documentDesignTokens.pdf.contract` (pt/mm) — parallel track, not identical units to preview.
 *
 * **Legacy (unchanged):** [public/contract-template.html](public/contract-template.html) still applies extra
 * modifiers (`.contract-body-p`, `.contract-body-p--tight`, `.contract-body-p--lg`, …) in `em`. Those are
 * not duplicated here; preview iframe overrides + this rhythm object are the **typed** source for new work
 * and for attachment Tailwind built from the same literals as the iframe.
 */

/**
 * Single source for `PREVIEW_OVERRIDE_STYLE` `main.contract-document` spacing literals.
 * Also drives attachment `space-y-[…]` where spacing must match **BodyParagraph** (1:1).
 */
export const documentPreviewContractIframeRhythm = {
  mainParagraphMarginBottom: "0.5rem",
  mainClauseParagraphMarginBottom: "1.25rem",
  clauseSubFirstMarginBottom: "0.35em",
  contatosBlockMarginEm: "1.25em",
  contatosFollowingBodyPMarginBottom: "0.28em",
  contatosInnerLineHeight: "1.125",
  contatosLineGap: "0.06em",
  contatosHeadingMargin: "0 0 0.12em 0",
  contractorIntroContactMarginBottom: "0.08em",
  contractCompactDataMarginBottom: "0.08em",
  clause7TailMarginTop: "0.5rem",
} as const;

/** Typography only — vertical margins live on `DocumentDate` via {@link DS_PREVIEW_ATTACHMENT_DOCUMENT_DATE_MARGIN_CLASS}. */
const DS_PREVIEW_ATTACHMENT_DOCUMENT_DATE_TYPOGRAPHY_CLASS =
  "text-center text-base" as const;

/**
 * Preview margins for `DocumentDate` (Local e data) — Pendrive footer rhythm: `mt-24` after body/list, `mb-12` before assinatura.
 */
const DS_PREVIEW_ATTACHMENT_DOCUMENT_DATE_MARGIN_CLASS =
  "mt-24 mb-12" as const;

const DS_PREVIEW_ATTACHMENT_DOCUMENT_DATE_LINE_CLASS =
  `${DS_PREVIEW_ATTACHMENT_DOCUMENT_DATE_TYPOGRAPHY_CLASS} ${DS_PREVIEW_ATTACHMENT_DOCUMENT_DATE_MARGIN_CLASS}` as const;

/**
 * Attachment preview: same font scale and justification habits as contract `.contract-doc-inner`, but
 * {@link DS_ATTACHMENT_PREVIEW_BODY_LINE_HEIGHT} for denser leading than clause `p` (1.22). Print uses
 * {@link documentDesignTokens.pdf.attachment.sheetBodyLineHeight}.
 */
export const DS_ATTACHMENT_STANDARD_BODY_PREVIEW_CLASS =
  `font-contract text-black text-[${DS_DOCUMENT_PREVIEW_INNER_FONT_SIZE_EM}em] leading-[${DS_ATTACHMENT_PREVIEW_BODY_LINE_HEIGHT}] antialiased text-justify [text-justify:inter-word] hyphens-none break-words` as const;

/**
 * Tailwind on `DocumentParagraph` preview (Abatimento / Cachê) — also the copy rhythm basis for the Cachê PIX label.
 */
export const DS_PREVIEW_ATTACHMENT_PARAGRAPH_CLASS =
  "text-justify mb-[2%] md:mb-4 leading-snug" as const;

/**
 * Preview title size in `rem` (Tailwind arbitrary `text-[…rem]`). PDF pt uses the same scale as the former
 * attachment heading pair: `text-base` (1rem) ↔ 10.5pt (`docTitleFontSizePt` before `.attachment-title`).
 */
export const DS_ATTACHMENT_TITLE_FONT_SIZE_REM = 1.09;

/**
 * Print/PDF title size (pt) for attachment sheets — preview uses {@link DS_ATTACHMENT_TITLE_FONT_SIZE_REM}.
 */
export const DS_ATTACHMENT_TITLE_FONT_SIZE_PT = 10.2 as const;

/**
 * Abatimento / Pendrive / Cachê title typography in preview (`DocumentTitle`).
 * Vertical spacing lives on {@link documentAttachmentPreview.titleBandClass} so the blue band hugs the text.
 */
export const documentAttachmentTitlePreviewClass =
  `text-[${DS_ATTACHMENT_TITLE_FONT_SIZE_REM}rem] font-bold text-center uppercase leading-snug` as const;

/**
 * Preview spacing + ✗ scale for attachment signatures (Pendrive assinatura row + Abatimento/Cachê dual columns).
 * Print: {@link documentDesignTokens.pdf.attachmentModelSignaturePrint}.
 */
export const DS_ATTACHMENT_MODEL_SIGNATURE_PREVIEW = {
  /** Margin above dual-column signature rows after date. */
  sectionMarginTopClass: "mt-24",
  /** Pendrive: label + underline + ✗. Gap before row comes from `DocumentDate` `mb-12`. */
  pendriveAssinaturaRowClass: "flex w-full items-end gap-4",
  /** ✗ in a single horizontal row (no extra margin below glyph). */
  markClass: "shrink-0 text-lg font-bold leading-none",
  /** ✗ stacked above rule in two-column blocks. */
  dualColumnMarkClass: "mb-[2%] md:mb-4 shrink-0 text-lg font-bold leading-none",
  /** Abatimento / Cachê model signature row — always one horizontal row (preview scales with sheet; no mobile stack). */
  dualSignRowClass:
    "mt-24 flex w-full flex-row items-end justify-between",
} as const;

/**
 * Print/PDF metrics for {@link DS_ATTACHMENT_MODEL_SIGNATURE_PREVIEW} — `attachment-print-html` only.
 */
export const DS_ATTACHMENT_MODEL_SIGNATURE_PRINT = {
  /** Matches preview `mt-24` (6rem). */
  sectionMarginTopRem: 6,
  /** Gap between Produtora / Contratante columns on `.sign-row`. */
  dualSignRowGapMm: 16,
  /** Pendrive assinatura row: label / line / ✗ — Tailwind `gap-4` (1rem). */
  pendriveInlineGapRem: 1,
  /** ✗ on `.sign-x` and `.pendrive-inline-x`. */
  markFontSizePt: 14,
} as const;

/**
 * Single dictionary: **preview** (Tailwind / iframe literals) vs **pdf** (print CSS).
 * Contract metrics are extracted verbatim; attachments consume the same tokens.
 */
export const documentDesignTokens = {
  preview: {
    /** Page-1 `.contract-logo` width token (iframe preview + Tailwind for attachments). */
    contractLogo: {
      maxWidthRem: documentLogoTokens.previewMaxWidthRem,
    } as const,
    /**
     * Re-exported as `CONTRACT_LAYOUT` from `lib/contract-template.ts` for PREVIEW/PRINT overrides.
     */
    contractLayout: {
      subindentEm: "3.02em",
      compactStackLineHeight: "1.125",
      compactStackGapPreviewEm: "0.06em",
      compactStackGapPrintPt: "1pt",
      modelQualInnerLineHeight: "1.08",
      modelQualInnerGapPreviewEm: "0.04em",
      modelQualInnerGapPrintPt: "0.75pt",
      modelQualBetweenBlocksPreviewEm: "0.75em",
      modelQualBetweenBlocksPrintPt: "9pt",
      previewBodyLineHeight: DS_DOCUMENT_PREVIEW_BODY_LINE_HEIGHT,
    } as const,
    /** Alias of {@link documentPreviewContractIframeRhythm} — `PREVIEW_OVERRIDE_STYLE` in `contract-template.ts`. */
    contractIframe: documentPreviewContractIframeRhythm,
    /**
     * Attachment document titles (Abatimento, Pendrive, Cachê) — Tailwind for {@link documentAttachmentTitlePreviewClass} / `DocumentTitle`.
     */
    attachmentTitle: documentAttachmentTitlePreviewClass,
    /**
     * Body copy `<p>` for Abatimento / Cachê preview — vertical rhythm + justify; pairs with {@link documentDesignTokens.pdf.attachmentParagraph} in print.
     */
    attachmentParagraph: DS_PREVIEW_ATTACHMENT_PARAGRAPH_CLASS,
    /**
     * React attachment preview — same base font scale as `.contract-doc-inner`; leading uses
     * {@link DS_ATTACHMENT_PREVIEW_BODY_LINE_HEIGHT} (denser than contract iframe). Stack gaps use
     * {@link documentPreviewContractIframeRhythm.mainParagraphMarginBottom}.
     */
    attachment: {
      /**
       * Legacy tooling/tests — same em scale + attachment leading as {@link attachmentStandardBodyPreviewClass}, without justify stack.
       */
      bodyTypographyClass: `font-contract text-black text-[${DS_DOCUMENT_PREVIEW_INNER_FONT_SIZE_EM}em] leading-[${DS_ATTACHMENT_PREVIEW_BODY_LINE_HEIGHT}] antialiased`,
      /**
       * Default body copy for Abatimento / Pendrive / Cachê **preview** — see {@link DS_ATTACHMENT_STANDARD_BODY_PREVIEW_CLASS}.
       */
      attachmentStandardBodyPreviewClass: DS_ATTACHMENT_STANDARD_BODY_PREVIEW_CLASS,
      /**
       * Same as {@link attachmentStandardBodyPreviewClass} — kept for `AttachmentDocumentFrame` / older imports.
       */
      attachmentSheetBodyTypographyClass: DS_ATTACHMENT_STANDARD_BODY_PREVIEW_CLASS,
      /** Column for {@link attachmentParagraph} blocks — no `space-y` (margins live on each `DocumentParagraph`). */
      bodyStackClass: "flex flex-col text-justify",
      /**
       * @deprecated Prefer `DocumentTitle` + {@link titleBandClass} — title text has no outer margins.
       */
      documentTitleClass: documentAttachmentTitlePreviewClass,
      /**
       * Blue band + vertical spacing **outside** the painted background (`mt-6 mb-10`); title text stays tight inside.
       */
      titleBandClass:
        "mt-[2%] md:mt-4 mb-[4%] md:mb-8 bg-[#c5d9f1] py-2 text-center",
      /** Cachê band heading — same typography as {@link documentDesignTokens.preview.attachmentTitle}. */
      titleBandHeadingClass: documentAttachmentTitlePreviewClass,
      logoWrapClass: "mb-5 flex justify-center",
      /** Like `.contract-logo` (no `width: 100%`) — intrinsic size up to max-width. */
      logoImgClass: `h-auto w-auto max-w-[${documentLogoTokens.previewMaxWidthRem}rem] object-contain mx-auto`,
      orderedListClass: `list-decimal space-y-[${documentPreviewContractIframeRhythm.mainParagraphMarginBottom}] pl-5`,
      dateLineCenterClass: DS_PREVIEW_ATTACHMENT_DOCUMENT_DATE_LINE_CLASS,
      /** @deprecated Prefer {@link attachmentDocumentDateLineClass} / `DocumentDate`. Kept alias for churn-free imports. */
      footerDateCenterClass: DS_PREVIEW_ATTACHMENT_DOCUMENT_DATE_LINE_CLASS,
      /** Full line (typography + margins) — legacy; `DocumentDate` uses typography + {@link attachmentDocumentDatePreviewMarginClass}. */
      attachmentDocumentDateLineClass: DS_PREVIEW_ATTACHMENT_DOCUMENT_DATE_LINE_CLASS,
      /** `DocumentDate` preview — typography without vertical margin. */
      attachmentDocumentDateTypographyClass:
        DS_PREVIEW_ATTACHMENT_DOCUMENT_DATE_TYPOGRAPHY_CLASS,
      /** `DocumentDate` preview — vertical margin (Pendrive-aligned). */
      attachmentDocumentDatePreviewMarginClass:
        DS_PREVIEW_ATTACHMENT_DOCUMENT_DATE_MARGIN_CLASS,
      modelBlockSectionClass: `mt-6 space-y-[${documentPreviewContractIframeRhythm.mainParagraphMarginBottom}]`,
      /**
       * Cachê — wraps the models grid; symmetric `my` vs telefone.
       */
      talentFeeFooterModelsSectionClass: "my-[4%] md:my-8 w-full min-w-0",
      /**
       * Cachê models — same two-column intent at all viewports (fluid sheet scales; no mobile stack).
       * `flex-wrap` still moves overflow rows below when many models.
       */
      talentFeeFooterModelsGridClass:
        "flex w-full min-w-0 flex-wrap justify-between gap-y-[4%] md:gap-y-8 mb-[4%] md:mb-8",
      /**
       * Per-cell: two columns at ~48%; `break-words` + `max-w-[48%]` keep long names inside the column
       * (no `min-w-0` on the cell — avoids flex min-size clipping; width is bounded by basis/max-w).
       */
      talentFeeFooterModelCellClass:
        "box-border max-w-[48%] flex-grow basis-[48%] break-words whitespace-normal",
      talentFeeFooterModelNameParagraphClass: "!mb-0 break-words whitespace-normal leading-tight",
      /** Cachê PIX row — gap below model name (hybrid mobile % / desktop rem, matches print rhythm). */
      talentFeeFooterPixRowClass:
        "mt-[2%] md:mt-4 flex w-full min-w-0 items-end",
      /**
       * Handwriting rule stroke — same as {@link documentDesignTokens.pdf.dualSignature.ruleClass}
       * / preview `dualSignatureRuleClass` (`border-b border-black`, 1px).
       */
      documentFillLinePreviewClass:
        "mb-0.5 min-h-[1px] min-w-[2rem] flex-1 self-end border-b border-black",
      /**
       * Last attachment body `<p>` before the models list — strips default paragraph `mb` to avoid double gap.
       */
      attachmentParagraphBeforeModelsClass: "!mb-0",
      /** Abatimento — model list; symmetric `my` vs body above and telefone below (matches Cachê section). */
      rebateModelsStackClass: "my-[4%] md:my-8 flex flex-col gap-1",
      /**
       * Space below the block immediately above "Telefone para contato" — same as the bottom half of
       * {@link talentFeeFooterModelsSectionClass} / {@link rebateModelsStackClass} (`my-[4%] md:my-8`).
       * Pendrive: assinatura row (flex: do not add `mt-*` on the phone line or margins double).
       */
      attachmentPhoneBlockAboveMarginClass: "mb-[4%] md:mb-8",
      rebateModelParagraphClass: "!mb-0 leading-tight",
      /**
       * Preview ✗ / Pendrive assinatura spacing — dual-column Abatimento/Cachê footer uses `dualSignature*`.
       */
      attachmentModelSignature: DS_ATTACHMENT_MODEL_SIGNATURE_PREVIEW,
      signatureRowClass: DS_ATTACHMENT_MODEL_SIGNATURE_PREVIEW.dualSignRowClass,
      signatureColumnClass: "w-[45%] min-w-0 shrink-0 text-center",
      signatureMarkClass: DS_ATTACHMENT_MODEL_SIGNATURE_PREVIEW.dualColumnMarkClass,
      signatureRuleClass: "border-t-2 border-black pt-4",
      signatureRuleThinClass:
        "w-full min-w-0 max-w-[45%] border-t-2 border-black pt-4",
      signatureLabelClass: "text-xs font-bold leading-tight",
      signatureMetaClass: "text-xs leading-tight",
      signatureRowTalentFeeClass: DS_ATTACHMENT_MODEL_SIGNATURE_PREVIEW.dualSignRowClass,
      signatureColumnCacheClass: "text-center",
      /**
       * Abatimento / Cachê dual signatures — `DocumentDualSignature` + print `.sign-dual*`.
       * (Pendrive / contrato usam outros tokens.)
       */
      dualSignatureContainerClass:
        "flex w-full flex-row items-end justify-between",
      dualSignatureMarginTopRem: 6.7,
      dualSignatureColumnClass: "w-[45%] min-w-0 shrink-0 text-center",
      dualSignatureRuleClass: "mb-2 w-full border-b border-black",
      dualSignatureTextClass:
        "break-words text-center text-xs font-bold uppercase",
      /** Matches print `.u-line` / contract rhythm (1.1em). */
      underlineFullWidthClass:
        "block w-full min-h-[1.1em] border-b border-black",
      underlineInlineClass:
        "inline-block min-h-[1.1em] border-b border-black align-bottom",
      /** Pendrive assinatura line — 2× legacy 1.1em tap for signing. */
      pendriveSignatureUnderlineClass:
        "block min-h-[2.2em] min-w-[2rem] flex-1 border-b border-black align-bottom",
      talentFeeModelNameUnderlineClass:
        "block min-h-[1.1em] min-w-[2rem] flex-1 border-b border-black self-end",
      talentFeeModelNameUnderlinePlaceholderClass:
        "block min-h-[1.1em] min-w-[2rem] flex-1 border-b border-black self-end",
      cachePixUnderlineClass:
        "block min-h-[1.1em] min-w-[2rem] flex-1 border-b border-black self-end",
      /** Legacy section break (e.g. Pendrive); Abatimento/Cachê telefone use model stack `mb-*` only — keep token for ad-hoc use. */
      attachmentSectionBreakMarginTopClass: "mt-[4%] md:mt-8",
      pendriveSignatureParagraphClass:
        "mt-0 flex flex-wrap items-end gap-2",
      /** @deprecated Prefer {@link attachmentModelSignature.markClass}. */
      pendriveSignMarkClass: DS_ATTACHMENT_MODEL_SIGNATURE_PREVIEW.markClass,
      /** Pendrive phone line — spacing comes from {@link attachmentPhoneBlockAboveMarginClass} on the row above. */
      pendrivePhoneParagraphClass: "mb-0 text-justify",
      /** Pendrive: wider gap between list items than generic `orderedListClass`. */
      pendriveOrderedListClass:
        "list-decimal pl-10 text-justify space-y-5 leading-snug",
      /** First body `p` when stack uses `gap-y-0` — same as BodyParagraph (`mainParagraphMarginBottom`). */
      pendriveFirstBodyParagraphClass: `mb-[${documentPreviewContractIframeRhythm.mainParagraphMarginBottom}]`,
      /** Margin below “declaro, para os devidos fins, que:” (≈2 body lines). */
      pendriveDeclaracaoTailMarginClass: `mb-[${DS_PENDRIVE_TWO_LINE_SPACER_EM}em]`,
      /** Margin above + below “Local e Data: …” (≈3 body lines each). */
      pendriveLocalDateVerticalMarginClass: `my-[${DS_PENDRIVE_THREE_LINE_SPACER_EM}em]`,
      /**
       * Pesquisa de Satisfação preview — mirrors {@link documentDesignTokens.pdf.attachmentSurvey}
       * density. Tighter than the shared attachment rhythm because this sheet is budgeted to one
       * A4 page (see `attachment-page-budget.ts`); `DocumentDate` margins are overridden via
       * `className` (tailwind-merge wins over the default `mt-24 mb-12`).
       */
      surveyLogoWrapClass: `mb-2 flex justify-center [&_img]:max-w-[${DS_SURVEY_LOGO_PREVIEW_MAX_WIDTH_REM}rem]`,
      surveyTitleBandClass:
        "mt-[1%] md:mt-2 mb-[1.5%] md:mb-3 bg-[#c5d9f1] py-1.5 text-center",
      surveyIssuerLineClass:
        "!mb-[1%] md:!mb-2 text-center font-semibold leading-snug",
      surveyIntroClass: "!mb-[2.5%] md:!mb-[0.9rem] leading-snug",
      surveyQuestionBlockClass: "mb-[2.5%] md:mb-[0.8rem]",
      surveyQuestionPromptClass:
        "!mb-[1%] md:!mb-[0.3rem] text-left leading-snug",
      /** Indented under the question — mirrors `pdf.attachmentSurvey.optionIndentRem`. */
      surveyOptionRowClass:
        "mb-[0.7%] md:mb-[0.22rem] last:!mb-0 ml-[0.9rem] flex items-start gap-[0.6rem] text-left leading-snug",
      /** Square hairline box, sized in `em` so it tracks the zoomed preview text. */
      surveyCheckboxClass:
        "mt-[0.28em] size-[0.9em] shrink-0 border border-black",
      surveyDeclarationClass: "mt-[3%] md:mt-[1.1rem] text-justify leading-snug",
      surveyDateMarginClass: "mt-[3%] md:mt-6 mb-[2%] md:mb-4",
      surveySignatureRowClass:
        "mt-[2%] md:mt-4 flex w-full items-end gap-2",
      surveySignatureLabelClass: "shrink-0 font-bold",
    } as const,
  },
  pdf: {
    contract: {
      rootFontSizePt: 11,
      rootLineHeight: 1.4,
      bodyParagraphLineHeight: 1.32,
      bodyParagraphMarginBottomPt: 4,
      clauseParagraphMarginBottomPt: 12,
      h1Pt: 14,
      h2Pt: 12,
      /** `h2.contract-p1-contratada-heading` — body-sized, not section bar. */
      h2ContratadaHeadingPt: 11,
      mainTitleLinePt: 10.5,
      pageMarginLandscapeShortMm: 15,
      pageMarginLandscapeLongMm: 20,
      /** `.contract-logo` print width — same as attachment `.doc-logo`. */
      logoWidthMm: documentLogoTokens.printWidthMm,
    } as const,
    attachment: {
      /** `@page` margin-top (attachment PDF only) — smaller than bottom to fit Cachê + 2 models on one A4. */
      pageMarginTopPrintMm: 10,
      /** `@page` margin-bottom (attachment PDF only). */
      pageMarginBottomPrintMm: 14,
      /**
       * Horizontal inset for **on-screen** attachment sheet (`DS_HOST_PREVIEW_PAPER_ATTACHMENT_INSET_CLASS` px).
       * Keep stable when tuning print-only margins.
       */
      pageMarginHorizontalMm: 16,
      /**
       * `@page` left/right margin in attachment **print/PDF only** — slightly tighter than `pageMarginHorizontalMm`
       * for a wider text column; does not affect React preview.
       */
      pageMarginHorizontalPrintMm: 22,
      docSheetMaxWidthMm: 190,
      /** Inner padding below `@page` top — paired with {@link pageMarginTopPrintMm} for preview `pt-[…]mm`. */
      docSheetPaddingTopMm: 4,
      docSheetPaddingBottomMm: 12,
      docLogoMarginBottomMm: 6,
      /** @deprecated Title spacing uses {@link documentDesignTokens.pdf.attachmentTitle} margins + `.attachment-title`. */
      docTitleMarginBottomMm: 8,
      docDateFontSizePt: 10.5,
      /** Print “Local e data” — matches preview `DocumentDate` rhythm (`mt-24` / `mb-12` ≈ mm). */
      docDateMarginTopMm: 25,
      docDateMarginBottomMm: 12,
      /** HTML `class` on the print `<p>` for attachment dates (stylesheet: `.doc-date-center`). */
      documentDatePrintBlockClass: "doc-date-center",
      modelBlockMarginTopMm: 5,
      modelLabelFontSizePt: 10.5,
      listPaddingLeftMm: 10,
      listItemMarginBottomEm: 0.55,
      /** Pendrive numbered list — looser than generic `listItemMarginBottomEm`. */
      pendriveListItemMarginBottomEm: 1,
      signLabelFontSizePt: 8.5,
      /** After main body blocks (telefone, “Local e data”). */
      docBodySectionBreakMarginTopMm: 8,
      pendriveSignatureBlockMarginTopMm: 6,
      docBodyTightFollowMarginTopMm: 5,
      /**
       * Symmetric vertical margin for Abatimento / Cachê **models block** in print (matches preview `md:my-8`).
       */
      attachmentModelsBlockVerticalMarginRem: 2,
      /**
       * Vertical gap between Cachê grid and overflow model rows in print — matches preview `md:gap-y-8` / `md:mb-8`.
       */
      attachmentModelsSectionInternalGapRem: 2,
      /**
       * Modifier on the last body `<p>` before models — `margin-bottom: 0` so block `margin-top` alone sets the gap.
       */
      attachmentParagraphBeforeModelsModifierClass:
        "attachment-doc-p--before-models",
      /** Wraps Cachê grid + overflow rows in `buildTalentFeePrintHtml`. */
      attachmentModelsSectionPrintClassName: "attachment-models-section",
      /**
       * `body { line-height }` in attachment print HTML only — not {@link pdf.contract.bodyParagraphLineHeight}.
       */
      sheetBodyLineHeight: 1.35,
    } as const,
    /**
     * Print Pendrive assinatura row / ✗ scale — Abatimento / Cachê dual footer uses `documentDesignTokens.pdf.dualSignature`.
     */
    attachmentModelSignaturePrint: DS_ATTACHMENT_MODEL_SIGNATURE_PRINT,
    /**
     * Print `.attachment-title` typography; vertical gaps apply to `.doc-title-band` so the band background stays tight.
     */
    attachmentTitle: {
      /** HTML `class` on print `<h1>` (`PRINT_STYLE` `.attachment-title`). */
      printClassName: "attachment-title",
      fontSizePt: DS_ATTACHMENT_TITLE_FONT_SIZE_PT,
      fontWeight: 700,
      letterSpacing: "0.02em",
      lineHeight: 1.2,
      /** `.doc-title-band { margin-top }` — outside the blue fill. */
      bandMarginTopRem: 1,
      /** `.doc-title-band { margin-bottom }` — Abatimento / Cachê. */
      bandMarginBottomRem: 2,
      /** `.doc-title-band--pendrive { margin-bottom }` — looser tail under Pendrive title. */
      pendriveBandMarginBottomRem: 4,
    } as const,
    /**
     * Body `<p>` for Abatimento / Cachê print — class + metrics mirrored in `attachment-print-html` `PRINT_STYLE`.
     */
    attachmentParagraph: {
      printClassName: "attachment-doc-p",
      marginBottomRem: 0.75,
      /** Matches {@link pdf.attachment.sheetBodyLineHeight} for uniform attachment density. */
      lineHeight: 1.35,
    } as const,
    /**
     * Pesquisa de Satisfação print metrics.
     *
     * This is the densest single-page attachment (5 questions × 3 options + declaration +
     * signature), so it overrides the shared footer rhythm — `attachment.docDateMarginTopMm`
     * (25mm) and `dualSignature.marginTopRem` (6.7rem) are tuned for sparse sheets and would
     * push this one onto a second page. Every value here is consumed by both the print CSS and
     * `estimateSatisfactionSurveyPrintHeightMm`, so the budget test tracks any change.
     */
    attachmentSurvey: {
      questionBlockClassName: "survey-question",
      questionPromptClassName: "survey-question__prompt",
      optionRowClassName: "survey-option",
      optionTextClassName: "survey-option__text",
      checkboxClassName: "survey-checkbox",
      issuerLineClassName: "survey-issuer",
      introClassName: "survey-intro",
      declarationClassName: "survey-declaration",
      signatureRowClassName: "survey-signature-row",
      signatureLabelClassName: "survey-signature-row__label",
      logoModifierClassName: "doc-logo--survey",
      titleBandModifierClassName: "doc-title-band--survey",
      /** Manual check box: drawn in CSS, never a `☐` glyph (serif print stack has no coverage). */
      checkboxSizeMm: 3.6,
      checkboxGapRem: 0.6,
      /** Drop needed to centre the box on the first option line (≈ (lineBox − boxSize) ÷ 2). */
      checkboxBaselineOffsetMm: 0.65,
      /**
       * Answers are indented under their question so the sheet reads as a hierarchy rather than a
       * flat wall of lines. Horizontal only — costs nothing against the single-page budget.
       */
      optionIndentRem: 0.9,
      promptLineHeight: 1.3,
      optionLineHeight: 1.25,
      /** Gap between a question and its first alternative — was 0, which made the block look welded. */
      promptMarginBottomRem: 0.3,
      questionMarginBottomRem: 0.8,
      optionMarginBottomRem: 0.22,
      issuerMarginBottomRem: 0.5,
      introMarginBottomRem: 0.9,
      declarationMarginTopRem: 1.1,
      /** Survey-specific footer — see the block comment above. */
      dateMarginTopMm: 6,
      dateMarginBottomMm: 5,
      signatureRowMarginTopRem: 1.4,
      /** Tighter than the shared `attachment.docLogoMarginBottomMm` (6mm). */
      logoMarginBottomMm: 3,
      /**
       * Narrower than the shared `documentLogoTokens.printWidthMm` (40mm). The mark is the tallest
       * single block on the sheet; trimming it funds the spacing this questionnaire needs while
       * still reading as the same letterhead as the other attachments.
       */
      logoWidthMm: DS_SURVEY_LOGO_PRINT_WIDTH_MM,
      titleBandMarginTopRem: 0.5,
      titleBandMarginBottomRem: 0.75,
      titleBandPaddingYPx: 6,
    } as const,
    /**
     * Abatimento / Cachê print — dual signature row (`buildDualSignaturePrintHtml`).
     */
    dualSignature: {
      blockClass: "sign-dual",
      columnClass: "sign-dual-col",
      ruleClass: "sign-dual-rule",
      textClass: "sign-dual-text",
      marginTopRem: 6.7,
      columnWidthPercent: 45,
      labelFontSizePt: 9,
    } as const,
    /**
     * Inline fill line (Cachê PIX, etc.) — same stroke as {@link dualSignature.ruleClass} in `PRINT_STYLE`.
     */
    documentFillLine: {
      printClassName: "document-fill-line",
    } as const,
    fontFamilyStack: DS_DOCUMENT_PDF_FONT_FAMILY_STACK,
  },
} as const;

/** Shorthand for attachment TSX — `documentDesignTokens.preview.attachment`. */
export const documentAttachmentPreview =
  documentDesignTokens.preview.attachment;

/**
 * PdfViewerClient — contract paper inset only. Fluid on small viewports; `lg:` matches PRINT `@page` 20mm H × 15mm V.
 */
export const DS_HOST_PREVIEW_PAPER_CONTRACT_INSET_CLASS =
  "px-6 py-6 sm:px-10 sm:py-10 lg:px-[20mm] lg:py-[15mm]" as const;

/**
 * PdfViewerClient — inset of the attachment **preview sheet only** (white “folha”).
 * Fluid `px` / `py` below `lg:`; `lg:` = PDF `@page` horizontal margin + composed vertical (same as before).
 * Does **not** change generated PDF HTML (`attachment-print-html` unchanged).
 */
export const DS_HOST_PREVIEW_PAPER_ATTACHMENT_INSET_CLASS =
  `px-6 pt-5 pb-6 sm:px-10 sm:pt-6 sm:pb-8 lg:px-[${documentDesignTokens.pdf.attachment.pageMarginHorizontalMm}mm] lg:pt-[${
    documentDesignTokens.pdf.attachment.pageMarginTopPrintMm +
    documentDesignTokens.pdf.attachment.docSheetPaddingTopMm
  }mm] lg:pb-[${
    documentDesignTokens.pdf.attachment.pageMarginBottomPrintMm +
    documentDesignTokens.pdf.attachment.docSheetPaddingBottomMm
  }mm]` as const;

/**
 * `FormSectionCard` header row: no horizontal inset on mobile (align with page gutter);
 * boxed card insets from `sm` (matches former `p-4` / `lg:p-6` rhythm on larger screens).
 */
export const DS_FORM_SECTION_CARD_HEADER_INSET_CLASS =
  "min-w-0 px-0 pb-4 sm:px-6 sm:pt-4 sm:pb-4 lg:px-6";

/**
 * `FormSectionCard` body: full-width fields on mobile; horizontal + bottom inset inside the
 * card from `sm` upward (`lg:` widens bottom/sides where the shell already uses `lg:p-6`).
 */
export const DS_FORM_SECTION_CARD_CONTENT_INSET_CLASS =
  "min-w-0 px-0 pt-0 pb-4 sm:px-6 sm:pb-6 lg:px-6 lg:pb-6";

/**
 * Padding on the **inner** column inside `[data-form-scroll]` — not on the overflow viewport (avoids
 * sticky/compositing clipping). Top: `pt-4` mobile; `lg:pt-0` + {@link DS_FORM_LEFT_COLUMN_SCROLL_TOP_INSET_CLASS} on desktop.
 */
export const DS_FORM_ROOT_SCROLL_PADDING_CLASS =
  "px-4 pb-4 pt-4 lg:px-6 lg:pb-6 lg:pt-0";

/**
 * Desktop left column: padding-top **above** the scroll root, **outside** `[data-form-scroll]`.
 * Keeps rhythm with `lg:p-6` without sitting inside `overflow-y-auto`.
 */
export const DS_FORM_LEFT_COLUMN_SCROLL_TOP_INSET_CLASS = "lg:pt-6";

/**
 * Top bar (histórico / ações) above the form.
 * Same inset as section cards (`p-4 lg:p-6`) so the bar aligns with form content below.
 */
export const DS_FORM_PAGE_HEADER_PADDING_CLASS = "p-4 lg:p-6";

/**
 * Shared minimum height for the form top bar and the PDF preview tab strip (`lg` split view).
 */
export const DS_FORM_PREVIEW_HEADER_MIN_HEIGHT_CLASS = "min-h-14";

/**
 * Inner row for split-view headers (histórico + abas do preview): same padding, min height, and
 * vertical centering so both columns align across the divider (avoid `items-stretch` on tabs).
 */
export const DS_SPLIT_VIEW_HEADER_INNER_CLASS = `flex w-full min-w-0 max-w-full items-center ${DS_FORM_PREVIEW_HEADER_MIN_HEIGHT_CLASS} ${DS_FORM_PAGE_HEADER_PADDING_CLASS}`;

/** Sticky footer: top/sides + bottom = block rhythm + safe-area (see globals CSS vars). */
export const DS_FORM_STICKY_FOOTER_PADDING_CLASS =
  "px-4 pt-4 lg:px-6 pb-[calc(var(--spacing-block)+var(--spacing-safe-area-bottom))]";

/** Section card title — compact on mobile; scales up from `sm` (pairs with viewport max-scale + form `text-sm`). */
export const DS_FORM_SECTION_TITLE_TEXT_CLASS =
  "text-sm font-semibold leading-snug tracking-tight text-primary sm:text-base lg:text-lg";

/** Lucide icon paired with section / subsection titles. */
export const DS_FORM_SECTION_ICON_CLASS =
  "size-5 shrink-0 text-angels-pink-brand";

/** Subsection heading inside a section card (same scale as {@link DS_FORM_SECTION_TITLE_TEXT_CLASS}). */
export const DS_FORM_SUBSECTION_TITLE_CLASS = DS_FORM_SECTION_TITLE_TEXT_CLASS;

/**
 * Gap between fields in `grid` layouts (single source for form column/row rhythm).
 * Use as `className={cn("grid grid-cols-1 md:grid-cols-2", DS_FORM_GRID_GAP_CLASS)}`.
 */
export const DS_FORM_GRID_GAP_CLASS = "gap-4 md:gap-6";

/**
 * Vertical stack inside `FormSectionCardContent` — `flex` + `gap` (no `space-y-*` margin collapse).
 */
export const DS_FORM_SECTION_STACK_GAP_CLASS = `flex flex-col ${DS_FORM_GRID_GAP_CLASS}`;

/**
 * Scroll column stack of section islands (`FormSectionCard`). Desktop: `gap-6` only. Mobile: a single
 * horizontal rule **between** sections (`border-t` on `* + *` only — never `divide-*`, which would
 * tint every border side and draw vertical lines on cards that still use `border` width).
 */
export const DS_FORM_SECTION_ISLAND_STACK_CLASS =
  "flex flex-col gap-6 max-sm:gap-0 max-sm:[&>*+*]:border-x-0 max-sm:[&>*+*]:border-b-0 max-sm:[&>*+*]:border-t max-sm:[&>*+*]:border-border/60 max-sm:[&>*+*]:pt-6";

/**
 * Padding-bottom from section title row to first content block.
 * `FormSectionCard` uses `DS_FORM_SECTION_CARD_HEADER_INSET_CLASS` for that spacing instead.
 */
export const DS_FORM_HEADER_TO_CONTENT_GAP_CLASS = "pb-4";

/** Gap between model card sub-blocks (title, info island, fields). */
export const DS_FORM_MODEL_CARD_BLOCK_GAP_CLASS = "mt-4";

/** Standard gap between icon (e.g. Lucide) and label text in flex rows. */
export const DS_FORM_ICON_TEXT_GAP_CLASS = "gap-3";

/** Informational callout (metadata hints, mirror notes). */
export const DS_FORM_INFO_CALLOUT_CLASS =
  "flex items-start gap-3 rounded-md border border-angels-purple/20 bg-angels-purple/10 px-3 py-2.5 text-sm leading-relaxed text-angels-purple";

/** Warning callout (e.g. clause 17). */
export const DS_FORM_WARNING_CALLOUT_CLASS =
  "flex items-start gap-3 rounded-lg border border-amber-200/60 bg-amber-50/50 px-3 py-2.5 text-sm leading-relaxed text-amber-600";

/** Grouped supplementary attachment cards (authorization section). */
export const DS_FORM_ATTACHMENT_GROUP_CLASS =
  "overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm";

export const DS_FORM_ATTACHMENT_GROUP_TITLE_CLASS =
  "px-4 pb-2 pt-4 text-sm font-semibold leading-snug tracking-tight text-primary";

export const DS_FORM_ATTACHMENT_TOGGLE_ROW_CLASS =
  "flex flex-row items-center justify-between px-4 py-4 max-sm:flex-col max-sm:items-stretch max-sm:gap-4";

export const DS_FORM_ATTACHMENT_EXTENSION_CLASS =
  "animate-in fade-in slide-in-from-top-1 border-t border-slate-200/80 bg-angels-purple/5 px-4 pb-4 pt-3 duration-200";

export const DS_FORM_ATTACHMENT_EXTENSION_CAPTION_CLASS =
  "mb-3 text-xs font-medium text-slate-500";

/** Login — Wing Aurora Light base (matches slate-50 family, slightly lavender). */
export const DS_LOGIN_WING_AURORA_BASE_COLOR = "#F8F7FC";

/**
 * Stacked radial layers for the login mesh — canonical RGB from tailwind `angels-*` tokens.
 * Consumed by `LoginBackground` (inline `backgroundImage`).
 */
export const DS_LOGIN_WING_AURORA_MESH_LAYERS = [
  "radial-gradient(ellipse 80% 60% at 85% 15%, rgb(131 208 242 / 0.18), transparent)",
  "radial-gradient(ellipse 70% 55% at 10% 85%, rgb(235 197 222 / 0.35), transparent)",
  "radial-gradient(ellipse 60% 50% at 50% 45%, rgb(102 78 162 / 0.08), transparent)",
] as const;

/**
 * Mobile portrait mesh — aurora concentrated above the card (premium header glow).
 * Slightly stronger opacity for small screens without extra DOM weight.
 */
export const DS_LOGIN_WING_AURORA_MESH_LAYERS_MOBILE = [
  "radial-gradient(ellipse 120% 70% at 50% -8%, rgb(131 208 242 / 0.24), transparent)",
  "radial-gradient(ellipse 90% 65% at 0% 100%, rgb(235 197 222 / 0.42), transparent)",
  "radial-gradient(ellipse 85% 55% at 100% 55%, rgb(102 78 162 / 0.11), transparent)",
] as const;

/** Login page shell — dvh + visual viewport var, safe areas, scrolls when content exceeds it.
 *  Vertical centering lives on the child via `my-auto`, not `justify-center`: auto margins
 *  collapse to 0 on overflow, keeping the card's top edge reachable at every viewport. */
export const DS_LOGIN_SHELL_CLASS =
  "relative flex min-h-dvh min-h-[var(--login-vvh,100dvh)] w-full flex-col items-center overflow-x-hidden overflow-y-auto px-3 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-[max(1rem,env(safe-area-inset-top))] sm:px-6 sm:pb-[max(2rem,env(safe-area-inset-bottom))] sm:pt-[max(2rem,env(safe-area-inset-top))]";

/** Immersive login card — solid white; softer radius/shadow on mobile. */
export const DS_LOGIN_CARD_CLASS =
  "w-full max-w-md overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-lg ring-1 ring-black/5 sm:rounded-xl sm:shadow-xl";

/** Slow mesh drift — pair with `@keyframes ds-login-mesh-drift` in globals.css. */
export const DS_LOGIN_MESH_DRIFT_CLASS = "animate-ds-login-mesh-drift";
